import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MessageSquare,
  Check,
  X,
  AlertCircle,
  Settings,
  Send,
  Loader2,
  ChevronRight,
  Shield,
  Clock,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const API = `${process.env.REACT_APP_API_URL}/api`;

// Channel definitions
const CHANNELS = [
  {
    id: "voice",
    name: "Voice Calls",
    description: "AI-powered inbound and outbound voice calls",
    icon: Phone,
    color: "emerald",
    status: "active",
    configurable: false
  },
  {
    id: "email",
    name: "Email Summaries",
    description: "Receive call summaries and lead notifications via email",
    icon: Mail,
    color: "blue",
    status: "active",
    configurable: false
  },
  {
    id: "whatsapp",
    name: "WhatsApp",
    description: "Call summaries and AI chatbot via WhatsApp Business",
    icon: MessageSquare,
    color: "emerald",
    status: "configurable",
    configurable: true
  }
];

const STATUS_STYLES = {
  active: { badge: "bg-emerald-500", text: "Active", icon: Check },
  configurable: { badge: "bg-amber-500", text: "Setup Required", icon: AlertCircle },
  coming_soon: { badge: "bg-gray-500", text: "Coming Soon", icon: Clock },
  disabled: { badge: "bg-gray-400", text: "Disabled", icon: X }
};

export default function Channels() {
  const { getAuthHeader } = useAuth();
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  
  // WhatsApp settings form
  const [summariesEnabled, setSummariesEnabled] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [notificationNumber, setNotificationNumber] = useState("");

  useEffect(() => {
    fetchWhatsAppStatus();
  }, []);

  const fetchWhatsAppStatus = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp/status`, { headers: getAuthHeader() });
      setWhatsappStatus(res.data);
      
      // Populate form with current settings
      const settings = res.data.business_settings || {};
      setSummariesEnabled(settings.summaries_enabled || false);
      setChatbotEnabled(settings.chatbot_enabled || false);
      setNotificationNumber(settings.notification_number || "");
    } catch (error) {
      console.error("Failed to fetch WhatsApp status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWhatsAppSettings = async () => {
    if (notificationNumber && !notificationNumber.startsWith("+")) {
      toast.error("Phone number must start with + (E.164 format)");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/whatsapp/settings`, {
        whatsapp_summaries_enabled: summariesEnabled,
        whatsapp_chatbot_enabled: chatbotEnabled,
        whatsapp_notification_number: notificationNumber || null
      }, { headers: getAuthHeader() });
      
      toast.success("WhatsApp settings saved");
      setShowWhatsAppDialog(false);
      fetchWhatsAppStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestWhatsApp = async () => {
    if (!notificationNumber) {
      toast.error("Please enter a notification number first");
      return;
    }

    setTesting(true);
    try {
      // Save settings first
      await axios.put(`${API}/whatsapp/settings`, {
        whatsapp_summaries_enabled: summariesEnabled,
        whatsapp_chatbot_enabled: chatbotEnabled,
        whatsapp_notification_number: notificationNumber
      }, { headers: getAuthHeader() });
      
      // Send test
      await axios.post(`${API}/whatsapp/test-summary`, {}, { headers: getAuthHeader() });
      toast.success("Test summary sent! Check your WhatsApp.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test");
    } finally {
      setTesting(false);
    }
  };

  const getChannelStatus = (channelId) => {
    if (channelId === "whatsapp") {
      if (!whatsappStatus?.platform_configured) {
        return "coming_soon";
      }
      if (whatsappStatus?.business_settings?.summaries_enabled) {
        return "active";
      }
      return "configurable";
    }
    return "active";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="channels-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">
          Communication Channels
        </h1>
        <p className="text-muted-foreground">
          Manage how Speakr communicates with you and your customers
        </p>
      </div>

      {/* Channels Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {CHANNELS.map((channel) => {
          const Icon = channel.icon;
          const status = getChannelStatus(channel.id);
          const statusStyle = STATUS_STYLES[status];
          const StatusIcon = statusStyle.icon;

          return (
            <Card 
              key={channel.id} 
              className={`relative overflow-hidden ${channel.configurable ? 'hover:border-primary/50 cursor-pointer' : ''}`}
              onClick={() => channel.id === "whatsapp" && whatsappStatus?.platform_configured && setShowWhatsAppDialog(true)}
              data-testid={`channel-${channel.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${channel.color}-500/10`}>
                    <Icon className={`w-6 h-6 text-${channel.color}-400`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{channel.name}</h3>
                      <Badge className={statusStyle.badge}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusStyle.text}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{channel.description}</p>
                  </div>
                </div>

                {channel.id === "whatsapp" && whatsappStatus?.platform_configured && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Call Summaries</span>
                      <Badge variant={summariesEnabled ? "default" : "outline"}>
                        {summariesEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full mt-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowWhatsAppDialog(true);
                      }}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Configure
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </Button>
                  </div>
                )}

                {channel.id === "whatsapp" && !whatsappStatus?.platform_configured && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-muted-foreground">
                      WhatsApp integration requires platform configuration. Contact support for setup.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* WhatsApp Info Card */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-400" />
            About WhatsApp Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Send className="w-4 h-4 text-primary" />
                Call Summaries
              </h4>
              <p className="text-sm text-muted-foreground">
                Receive AI-generated call summaries instantly on WhatsApp. Perfect for staying 
                informed about customer interactions when you're away from your desk.
              </p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-primary" />
                AI Chatbot
                <Badge variant="outline" className="text-xs">Coming Soon</Badge>
              </h4>
              <p className="text-sm text-muted-foreground">
                Let your AI agents respond to customer WhatsApp messages automatically, 
                with the same intelligence as your voice agents.
              </p>
            </div>
          </div>

          <Alert>
            <Shield className="w-4 h-4" />
            <AlertTitle>Compliance Note</AlertTitle>
            <AlertDescription>
              WhatsApp messages follow Meta's Business Platform guidelines. Template messages 
              are used for business-initiated conversations to ensure compliance.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* WhatsApp Settings Dialog */}
      <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="whatsapp-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-400" />
              WhatsApp Settings
            </DialogTitle>
            <DialogDescription>
              Configure how you receive WhatsApp notifications from Speakr
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Notification Number */}
            <div className="space-y-2">
              <Label htmlFor="whatsapp-number">WhatsApp Number</Label>
              <Input
                id="whatsapp-number"
                placeholder="+1234567890"
                value={notificationNumber}
                onChange={(e) => setNotificationNumber(e.target.value)}
                data-testid="whatsapp-number-input"
              />
              <p className="text-xs text-muted-foreground">
                Enter your WhatsApp number in E.164 format (e.g., +1234567890)
              </p>
            </div>

            {/* Call Summaries Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <h4 className="font-medium">Call Summaries</h4>
                <p className="text-sm text-muted-foreground">
                  Receive AI-generated summaries after each call
                </p>
              </div>
              <Switch
                checked={summariesEnabled}
                onCheckedChange={setSummariesEnabled}
                data-testid="summaries-toggle"
              />
            </div>

            {/* AI Chatbot Toggle (Coming Soon) */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg opacity-60">
              <div>
                <h4 className="font-medium flex items-center gap-2">
                  AI Chatbot
                  <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                </h4>
                <p className="text-sm text-muted-foreground">
                  AI responds to customer WhatsApp messages
                </p>
              </div>
              <Switch
                checked={chatbotEnabled}
                onCheckedChange={setChatbotEnabled}
                disabled
                data-testid="chatbot-toggle"
              />
            </div>

            {/* Test Button */}
            {notificationNumber && (
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={handleTestWhatsApp}
                disabled={testing}
                data-testid="test-whatsapp-btn"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {testing ? "Sending Test..." : "Send Test Summary"}
              </Button>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowWhatsAppDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveWhatsAppSettings} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
